import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/theme';
import { 
  FaShip, 
  FaHeart, 
  FaRocket,
  FaShieldAlt
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['5xl']};
  margin-bottom: ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['3xl']};
  margin-bottom: ${theme.spacing['2xl']};
  color: ${theme.colors.text.primary};
  text-align: center;
`;

const StoryCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['3xl']};
  backdrop-filter: blur(10px);
  margin-bottom: ${theme.spacing['2xl']};
`;

const StoryText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.lg};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.xl};
`;

const ValueCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.1);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing['2xl']};
  text-align: center;
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${theme.colors.primary.main};
    box-shadow: ${theme.shadows.lg};
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.lg};
`;

const ValueTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const ValueDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const TeamSection = styled.div`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['3xl']};
  text-align: center;
`;

const TeamText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  max-width: 800px;
  margin: 0 auto;
`;

const PoweredBy = styled.div`
  margin-top: ${theme.spacing['2xl']};
  padding: ${theme.spacing.lg};
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  text-align: center;
`;

const PoweredByText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const PoweredByLink = styled.a`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semibold};
  
  &:hover {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin: ${theme.spacing['2xl']} 0;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const About = () => {
  const values = [
    {
      icon: <FaShip />,
      title: "Marine Expertise",
      description: "NMEA certified, ABYC Certified Marine Electrical, with real-world experience cruising the Trent Severn system."
    },
    {
      icon: <FaShieldAlt />,
      title: "Proven Reliability",
      description: "Built with decades of technology experience and marine industry certifications for dependable performance."
    },
    {
      icon: <FaHeart />,
      title: "Passion for Boating",
      description: "From 12 years old with a 6hp Chrysler motor to full-time marine technician - boating is in our blood."
    },
    {
      icon: <FaRocket />,
      title: "Technology Innovation",
      description: "35+ years in technology, from IBM to White House showcases, now bringing that expertise to marine systems."
    }
  ];

  return (
    <Container>
      <Header>
        <Title>About BoatWise</Title>
        <Subtitle>
          Combining two passions: cutting-edge technology and marine systems expertise.
        </Subtitle>
      </Header>

      <Section>
        <SectionTitle>Peter's Story</SectionTitle>
        <StoryCard
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <StoryText>
            After 35+ years in the corporate world, Peter decided it was time to make a change. Peter spent 35+ years 
            evangelizing technology in the education world. He worked at IBM out of college testing operating systems 
            like OS/2 and then spent 11 years at Seneca College building networks across multiple campuses, building 
            online learning courses and platforms and supporting thousands of students in learning online.
          </StoryText>
          <StoryText>
            Peter then went on to companies like Blackboard and Pearson to evangelize technology solutions around 
            the globe, and to expand access to education, with a keen focus on helping to make it simple. Peter was 
            even invited to the White House in 2008 to showcase a learning content platform that allowed academic 
            institutions to share content around the globe.
          </StoryText>
          <StoryText>
            At home, he experimented with home automation back in the early 2000s and even built a voice recognition 
            system years before Google and Alexa were even household names.
          </StoryText>
        </StoryCard>
      </Section>

      <Section>
        <SectionTitle>From Passion to Profession</SectionTitle>
        <StoryCard
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <StoryText>
            Peter's passion for boating started at 12 years old when he saved enough money to buy a 6hp Chrysler 
            motor and drop that onto a 12' aluminum boat to head out on the water at the family cottage. Of course, 
            he had to rebuild the carburetor on that motor to make it run better.
          </StoryText>
          <StoryText>
            He started cruising the Trent Severn system in the mid 2000s and continues to explore. Over the years 
            he has always revamped systems on his and colleagues' boats and decided in 2022 to take the leap and 
            do this full-time.
          </StoryText>
          <StoryText>
            Peter is now a full-time marine electrical and electronics technician, he is NMEA (National Marine 
            Electronics Association) certified, ABYC Certified Marine Electrical, Authorized Garmin Marine installer 
            and continues to expand his knowledge with his general passion for learning and sharing.
          </StoryText>
        </StoryCard>
      </Section>

      <Section>
        <SectionTitle>Our Values</SectionTitle>
        <ValuesGrid>
          {values.map((value, index) => (
            <ValueCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <ValueIcon>{value.icon}</ValueIcon>
              <ValueTitle>{value.title}</ValueTitle>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValuesGrid>
      </Section>

      <Section>
        <SectionTitle>Why Choose BoatWise?</SectionTitle>
        <StatsGrid>
          <StatItem>
            <StatNumber>99.9%</StatNumber>
            <StatLabel>Uptime</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>Support</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>1000+</StatNumber>
            <StatLabel>Happy Boaters</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>5★</StatNumber>
            <StatLabel>Rating</StatLabel>
          </StatItem>
        </StatsGrid>
        
        <TeamSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionTitle>Peter's Vision</SectionTitle>
          <TeamText style={{ fontStyle: 'italic', fontSize: theme.typography.fontSize.lg, textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
            "I have been watching this convergence of technology move its way into the boating world. I see tech that we were 
            working on years ago in the education space and now making its way into the mainstream boating world. Boats don't 
            rust, we can upgrade them! We don't have to use them as-is, we can modernize them!"
          </TeamText>
          <TeamText>
            BoatWise is powered by Modern Marine, bringing together Peter's 35+ years of technology expertise with his 
            passion for marine systems. From IBM to the White House, from education technology to marine electronics - 
            Peter's unique background creates the perfect foundation for next-generation marine monitoring.
          </TeamText>
          <PoweredBy>
            <PoweredByText>
              Learn more about <PoweredByLink href="https://www.modernmarine.ca" target="_blank" rel="noopener noreferrer">Modern Marine</PoweredByLink> - 
              Your trusted partner in marine technology
            </PoweredByText>
          </PoweredBy>
        </TeamSection>
      </Section>
    </Container>
  );
};

export default About;
